// BusinessServicePage.js

import React, { useState, useEffect } from 'react';
import {
  Layout,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Card,
  Typography,
  Space,
  message,
  Table,
  Modal,
  Upload,
  Spin,
  Descriptions,
  Popconfirm,
  Drawer,
  Tabs,
  List,
  DatePicker,
  Calendar,
  Badge
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  CalendarOutlined,
  MessageOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/DBContext'; // Adjust the import path as needed
import moment from 'moment';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { auth, db, storage, functions} from "../../firebase_setup"

dayjs.extend(isSameOrBefore);


const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const BusinessServicePage = () => {
  // Import necessary functions from useAuth
  const {
    currentUser,
    fetchServicesClaimedByBusiness,
    createService,
    getUserInfo,
    deleteService,
    updateService,
    uploadServiceImage,
    createTimeSlot,
    fetchTimeSlotsWithBookings,
    updateTimeSlot,
    deleteTimeSlot,
    updateBookingStatus,
    sendMessage
  } = useAuth();
  

  // State variables
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [form] = Form.useForm();
  const [editingService, setEditingService] = useState(null);
  const [isServiceModalVisible, setIsServiceModalVisible] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [markerImageFile, setMarkerImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [markerImagePreview, setMarkerImagePreview] = useState(null);
// Add these state variables alongside your existing state declarations
const [bookingFilterAllBookings, setBookingFilterAllBookings] = useState('All'); // Options: All, Confirmed, Completed
const [bookingFiltersPerTimeSlot, setBookingFiltersPerTimeSlot] = useState({}); // Key: timeSlotId, Value: filter

    // Define normFile function
    const normFile = (e) => {
      if (Array.isArray(e)) {
        return e;
      }
      return e && e.fileList ? e.fileList : [];
    };

    
  // Fetch services claimed by the business user
  useEffect(() => {
    async function loadServices() {
      setLoadingServices(true);
      try {
        const servicesData = await fetchServicesClaimedByBusiness(currentUser.uid);
        setServices(servicesData);
      } catch (error) {
        message.error('Failed to load services.');
        // console.error('Error:', error);
      } finally {
        setLoadingServices(false);
      }
    }
    if (currentUser) {
      loadServices();
    }
  }, [currentUser]);

  // Handle creating a new service
  const handleCreateService = () => {
    setEditingService(null);
    form.resetFields();
    form.setFieldsValue({
      image: [],
      marker_image: [],
    });
    setImageFile(null);
    setMarkerImageFile(null);
    setImagePreview(null);
    setMarkerImagePreview(null);
    setIsServiceModalVisible(true);
  };
  
  const handleEditService = (service) => {
    setEditingService(service);
    form.setFieldsValue({
      name: service.name,
      description: service.description,
      category: service.category,
      email: service.email,
      address: service.address,
      phone_number: service.phone_number,
      website: service.website,
      activities: service.activities,
      beneficiaries: service.beneficiaries,
      price: service.price,
      image: service.image
        ? [
            {
              uid: '-1',
              name: 'service-image.png',
              status: 'done',
              url: service.image,
            },
          ]
        : [],
      marker_image: service.marker_image
        ? [
            {
              uid: '-2',
              name: 'marker-image.png',
              status: 'done',
              url: service.marker_image,
            },
          ]
        : [],
    });
    setImagePreview(service.image || null);
    setMarkerImagePreview(service.marker_image || null);
    setIsServiceModalVisible(true);
  };
  
  

  // Handle deleting a service
  const handleDeleteService = async (serviceId) => {
    setIsLoadingAction(true);
    try {
      await deleteService(serviceId);
      setServices(services.filter((service) => service.id !== serviceId));
      message.success('Service deleted successfully!');
    } catch (error) {
      // message.error('Failed to delete service.');
      // console.error('Error:', error);
    } finally {
      setIsLoadingAction(false);
    }
  };


  // Use uploadServiceImage in handleServiceFormSubmit
const handleServiceFormSubmit = async (values) => {
  setIsLoadingAction(true);
  try {
    let serviceId;
    const { image, marker_image, ...rest } = values;

    let imageUrl = editingService ? editingService.image : '';
    let markerImageUrl = editingService ? editingService.marker_image : '';

    // Handle image upload
    if (image && image.length > 0) {
      const fileObj = image[0];
      if (fileObj.originFileObj) {
        imageUrl = await uploadServiceImage(fileObj.originFileObj);
      } else if (fileObj.url) {
        imageUrl = fileObj.url; // Existing image URL
      }
    } else {
      imageUrl = ''; // If image is removed
    }

    // Handle marker image upload
    if (marker_image && marker_image.length > 0) {
      const fileObj = marker_image[0];
      if (fileObj.originFileObj) {
        markerImageUrl = await uploadServiceImage(fileObj.originFileObj);
      } else if (fileObj.url) {
        markerImageUrl = fileObj.url; // Existing marker image URL
      }
    } else {
      markerImageUrl = ''; // If marker image is removed
    }

    const serviceData = {
      ...rest,
      image: imageUrl,
      marker_image: markerImageUrl,
      price: values.price,

    };

    if (editingService) {
      serviceId = await updateService(editingService.id, serviceData);
      message.success('Service updated successfully!');
    } else {
      serviceId = await createService(serviceData);
      message.success('Service created successfully!');
    }

    // Refresh services list
    const servicesData = await fetchServicesClaimedByBusiness(currentUser.uid);
    setServices(servicesData);
    setIsServiceModalVisible(false);
  } catch (error) {
    message.error('Failed to save service.');
    // console.error('Error:', error);
  } finally {
    setIsLoadingAction(false);
  }
};

  // Handle viewing service details
  const handleViewServiceDetails = (service) => {
    Modal.info({
      title: 'Service Details',
      width: 600,
      content: (
        <Descriptions
          column={1}
          bordered
          size="small"
          labelStyle={{ width: '200px' }}
        >
          {/* Basic Info */}
          <Descriptions.Item label="Service Name">{service.name}</Descriptions.Item>
          <Descriptions.Item label="Description">{service.description}</Descriptions.Item>

          {/* Display Categories */}
          <Descriptions.Item label="Categories">
            {service.category && service.category.length > 0
              ? service.category.map((category, index) => (
                  <span key={index} style={{ display: 'inline-block', marginRight: '8px' }}>
                    {category}
                  </span>
                ))
              : 'No categories selected'}
          </Descriptions.Item>

          {/* Contact Info */}
          <Descriptions.Item label="Email">{service.email}</Descriptions.Item>
          <Descriptions.Item label="Address">{service.address}</Descriptions.Item>
          <Descriptions.Item label="Phone Number">{service.phone_number}</Descriptions.Item>
          <Descriptions.Item label="Website">
            {service.website ? (
              <a href={service.website} target="_blank" rel="noopener noreferrer">
                {service.website}
              </a>
            ) : (
              'No website provided'
            )}
          </Descriptions.Item>

          {/* Activities */}
          <Descriptions.Item label="Activities">
            {service.activities && service.activities.length > 0
              ? service.activities.map((activity, index) => (
                  <span key={index} style={{ display: 'inline-block', marginRight: '8px' }}>
                    {activity}
                  </span>
                ))
              : 'No activities selected'}
          </Descriptions.Item>

          {/* Beneficiaries */}
          <Descriptions.Item label="Beneficiaries">
            {service.beneficiaries && service.beneficiaries.length > 0
              ? service.beneficiaries.map((beneficiary, index) => (
                  <span key={index} style={{ display: 'inline-block', marginRight: '8px' }}>
                    {beneficiary}
                  </span>
                ))
              : 'No beneficiaries selected'}
          </Descriptions.Item>
          <Descriptions.Item label="Price">${service.price}</Descriptions.Item>

          {/* Service Images */}
          <Descriptions.Item label="Service Image">
            {service.image ? (
              <img src={service.image} alt="Service" style={{ width: '100px', height: '100px' }} />
            ) : (
              'No image available'
            )}
          </Descriptions.Item>

          {/* Marker Image */}
          <Descriptions.Item label="Marker Image">
            {service.marker_image ? (
              <img src={service.marker_image} alt="Marker" style={{ width: '100px', height: '100px' }} />
            ) : (
              'No marker image available'
            )}
          </Descriptions.Item>
        </Descriptions>
      ),
      onOk() {},
    });
  };

  // Table columns configuration
  const serviceColumns = [
    {
      title: 'Service Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <a onClick={() => handleViewServiceDetails(record)} style={{ fontSize: '12px' }}>{text}</a>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      sorter: (a, b) => a.address.localeCompare(b.address),
      render: text => <div style={{ fontSize: '12px' }}>{text}</div>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click
              handleEditService(record);
            }}
            type="primary"
            size="small"
            style={{ fontSize: '12px' }} // Adjust the button text size if needed
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this service?"
            onConfirm={(e) => {
              e.stopPropagation(); // Prevent row click
              handleDeleteService(record.id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              type="danger"
              size="small"
              onClick={(e) => e.stopPropagation()} // Prevent row click
              style={{ fontSize: '12px' }} // Adjust the button text size if needed
            >
              Delete
            </Button>
          </Popconfirm>
          <Button
            icon={<CalendarOutlined />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click
              handleManageTimeSlots(record);
            }}
            type="default"
            size="small"
          >
            Bookings
          </Button>
        </Space>
      ),
    },
];

  
  useEffect(() => {
    // Cleanup object URLs when component unmounts or imagePreview changes
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      if (markerImagePreview) {
        URL.revokeObjectURL(markerImagePreview);
      }
    };
  }, [imagePreview, markerImagePreview]);
  

  // booking state
  const [selectedService, setSelectedService] = useState(null);
  const [isTimeSlotDrawerVisible, setIsTimeSlotDrawerVisible] = useState(false);
  const [timeSlotForm] = Form.useForm();
  const [selectedDate, setSelectedDate] = useState(moment()); // Initialize to today
  const [dateRange, setDateRange] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);


  const handleManageTimeSlots = (service) => {
    setSelectedService(service);
    setIsTimeSlotDrawerVisible(true);
    // Clear previous time slots and selected date
    setTimeSlots([]);
    setSelectedDate(null);
  };
  const handleAddTimeSlot = () => {
    timeSlotForm.resetFields();
    Modal.confirm({
      title: 'Add New Time Slots',
      content: (
        <Form form={timeSlotForm} layout="vertical">
          {/* Form Items */}
          <Form.Item
            name="dateRange"
            label="Select Date Range"
            rules={[{ required: true, message: 'Please select a date range!' }]}
          >
            <DatePicker.RangePicker
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>
          <Form.Item
            name="startTime"
            label="Start Time"
            rules={[{ required: true, message: 'Please enter the start time!' }]}
          >
            <Input placeholder="e.g., 9:00 AM" />
          </Form.Item>
          <Form.Item
            name="endTime"
            label="End Time"
            rules={[{ required: true, message: 'Please enter the end time!' }]}
          >
            <Input placeholder="e.g., 10:00 AM" />
          </Form.Item>
          <Form.Item
            name="availability"
            label="Availability"
            rules={[{ required: true, message: 'Please enter the availability!' }]}
          >
            <InputNumber min={1} placeholder="e.g., 4" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        setIsLoadingAction(true);
        try {
          const values = await timeSlotForm.validateFields();
          const { dateRange, ...timeSlotValues } = values;
  
          // Ensure dateRange is valid
          if (!dateRange || dateRange.length !== 2) {
            throw new Error('Invalid date range selected.');
          }
  
          if (!selectedService || !selectedService.id) {
            message.error('No service selected.');
            return;
          }
  
          // console.log('Validated values:', values);
          // console.log('Selected Service:', selectedService);
  
          const start = dayjs(dateRange[0]).startOf('day');
          const end = dayjs(dateRange[1]).startOf('day');
  
          const dates = [];
  
          let currentDate = start.clone();
          while (currentDate.isSameOrBefore(end)) {
            dates.push(currentDate.clone());
            currentDate = currentDate.add(1, 'day');
          }
  
          for (const date of dates) {
            const timeSlotData = {
              ...timeSlotValues,
              date: date.format('YYYY-MM-DD'),
              serviceId: selectedService.id,
            };
            await createTimeSlot(selectedService.id, timeSlotData);
          }
  
          message.success('Time slots added successfully!');
  
          // Refresh time slots for the selected date
          if (selectedDate === start.format('YYYY-MM-DD')) {
            const updatedTimeSlots = await fetchTimeSlotsWithBookings(
              selectedService.id,
              selectedDate
            );
            setTimeSlots(updatedTimeSlots);
          }
        } catch (error) {
          message.error('Failed to add time slots.');
          // console.error('Error:', error);
        } finally {
          setIsLoadingAction(false);
        }
      },
      onCancel() {},
      okText: 'Add',
      cancelText: 'Cancel',
    });
  };
  
  const handleEditTimeSlot = (timeSlot) => {
    timeSlotForm.setFieldsValue({
      startTime: timeSlot.startTime,
      endTime: timeSlot.endTime,
      availability: timeSlot.availability,
    });
    Modal.confirm({
      title: `Edit Time Slot for ${selectedDate}`,
      content: (
        <Form form={timeSlotForm} layout="vertical">
<Form.Item
          name="startTime"
          label="Start Time"
          rules={[{ required: true, message: 'Please enter the start time!' }]}
        >
          <Input placeholder="e.g., 11:00 AM" />
        </Form.Item>
        <Form.Item
          name="endTime"
          label="End Time"
          rules={[{ required: true, message: 'Please enter the end time!' }]}
        >
          <Input placeholder="e.g., 11:30 AM" />
        </Form.Item>
        <Form.Item
          name="availability"
          label="Availability"
          rules={[{ required: true, message: 'Please enter the availability!' }]}
        >
          <InputNumber min={1} placeholder="e.g., 4" style={{ width: '100%' }} />
        </Form.Item>        </Form>
      ),
      onOk: () => {
        timeSlotForm.validateFields().then(async (values) => {
          setIsLoadingAction(true);
          try {
            await updateTimeSlot(selectedService.id, timeSlot.id, values);
            message.success('Time slot updated successfully!');
            // Refresh time slots
            const updatedTimeSlots = await fetchTimeSlotsWithBookings(selectedService.id, selectedDate);
            setTimeSlots(updatedTimeSlots);
          } catch (error) {
            message.error('Failed to update time slot.');
            // console.error('Error:', error);
          } finally {
            setIsLoadingAction(false);
          }
        });
      },
      onCancel() {},
      okText: 'Save',
      cancelText: 'Cancel',
    });
  };
  
  const handleDeleteTimeSlot = (timeSlotId) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this time slot? All associated bookings will also be deleted.',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        setIsLoadingAction(true);
        try {
          await deleteTimeSlot(selectedService.id, timeSlotId);
          message.success('Time slot deleted successfully!');
          // Refresh time slots
          const updatedTimeSlots = await fetchTimeSlotsWithBookings(selectedService.id, selectedDate);
          setTimeSlots(updatedTimeSlots);
        } catch (error) {
          message.error('Failed to delete time slot.');
          // console.error('Error:', error);
        } finally {
          setIsLoadingAction(false);
        }
      },
    });
  };
  


  const handleCancelBooking = async (bookingId, timeSlotId) => {
    try {
      await updateBookingStatus(selectedService.id, timeSlotId, bookingId, 'Cancelled');
      // Refresh time slots
      const updatedTimeSlots = await fetchTimeSlotsWithBookings(selectedService.id, selectedDate);
      setTimeSlots(updatedTimeSlots);
      message.success('Booking canceled successfully!');
    } catch (error) {
      message.error('Failed to cancel booking.');
      // console.error('Error:', error);
    }
  };
  
  

  /**
   * Columns configuration for the User Bookings Table
   */const userBookingColumns = [
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    key: 'customerName',
    render: (text) => <b>{text}</b>,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'Booking Time',
    dataIndex: 'bookingTime',
    key: 'bookingTime',
  },
  {
    title: 'Price',
    dataIndex: 'price',
    key: 'price',
  },
  {
    title: 'Status',
    dataIndex: 'bookingStatus',
    key: 'bookingStatus',
    filters: [
      { text: 'Confirmed', value: 'Confirmed' },
      { text: 'Pending', value: 'Pending' },
      { text: 'Cancelled', value: 'Cancelled' },
      { text: 'Completed', value: 'Completed' },
    ],
    onFilter: (value, record) => record.bookingStatus === value,
    render: (text) => (
      <span
        style={{
          color:
            text === 'Confirmed'
              ? 'green'
              : text === 'Pending'
              ? 'orange'
              : text === 'Cancelled'
              ? 'red'
              : 'blue',
        }}
      >
        {text}
      </span>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => (
      <Space size="middle">
        <Button
          icon={<MessageOutlined />}
          onClick={() => handleMessageUser(record)}
          type="default"
          size="small"
        >
          Message
        </Button>
        {/* <Popconfirm
          title="Are you sure to cancel this booking?"
          onConfirm={() => handleCancelBooking(record.id, record.timeSlotId)}
          okText="Yes"
          cancelText="No"
        >
          <Button icon={<CloseCircleOutlined />} type="danger" size="small">
            Cancel
          </Button>
        </Popconfirm> */}
        {/* Removed 'Rearrange' Button */}
      </Space>
    ),
  },
];

  
  




const handleCompleteBooking = async (bookingId, timeSlotId) => {
  try {
    // Implement your logic to mark the booking as completed
    // Example: await updateBookingStatus(selectedService.id, timeSlotId, bookingId, 'Completed');
    message.success('Booking marked as completed!');
    // Refresh time slots
    const timeSlotsData = await fetchTimeSlotsWithBookings(selectedService.id);
    setTimeSlots(timeSlotsData);
  } catch (error) {
    message.error('Failed to complete booking.');
    // console.error('Error:', error);
  }
};


// Function to reset availability
const renderTimeSlotsByDate = () => {
  // Group time slots by date
  const timeSlotsByDate = timeSlots.reduce((groups, timeSlot) => {
    const date = timeSlot.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(timeSlot);
    return groups;
  }, {});

  // Sort dates
  const sortedDates = Object.keys(timeSlotsByDate).sort((a, b) => new Date(a) - new Date(b));

  return sortedDates.length > 0 ? (
    <Tabs>
      {sortedDates.map((date) => (
        <TabPane tab={date} key={date}>
          <List
            itemLayout="horizontal"
            dataSource={timeSlotsByDate[date]}
            renderItem={(timeSlot) => (
              <List.Item>
                <List.Item.Meta
                  title={`${timeSlot.startTime} - ${timeSlot.endTime}`}
                  description={`Availability: ${timeSlot.availability}`}
                />
                {/* Action Buttons */}
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => handleCompleteAllBookings(timeSlot.id)}
                    size="small"
                  >
                    Complete All
                  </Button>
                  <Button
                    type="default"
                    icon={<SyncOutlined />}
                    onClick={() => handleResetAvailability(timeSlot.id)}
                    size="small"
                  >
                    Reset Availability
                  </Button>
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    onClick={() => handleChangeAvailability(timeSlot)}
                    size="small"
                  >
                    Change Availability
                  </Button>
                  <Popconfirm
                 
                 title="Are you sure to delete this time slot? All associated bookings will also be deleted."
                    onConfirm={() => handleDeleteTimeSlot(timeSlot.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button icon={<DeleteOutlined />} type="danger" size="small">
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              </List.Item>
            )}
          />
        </TabPane>
      ))}
    </Tabs>
  ) : (
    <p>No time slots available.</p>
  );
};


const renderTimeSlotActions = (timeSlot) => [
  <Button
    type="primary"
    icon={<CheckOutlined />}
    onClick={() => handleCompleteAllBookings(timeSlot.id)}
    size="small"
  >
    Complete All
  </Button>,
  <Button
    type="default"
    icon={<SyncOutlined />}
    onClick={() => handleResetAvailability(timeSlot.id)}
    size="small"
  >
    Reset Availability
  </Button>,
  <Button
    type="default"
    icon={<EditOutlined />}
    onClick={() => handleChangeAvailability(timeSlot)}
    size="small"
  >
    Change Availability
  </Button>,
  <Popconfirm
    title="Are you sure to delete this time slot? All associated bookings will also be deleted."
    onConfirm={() => handleDeleteTimeSlot(timeSlot.id)}
    okText="Yes"
    cancelText="No"
  >
    <Button icon={<DeleteOutlined />} type="danger" size="small">
      Delete
    </Button>
  </Popconfirm>,
];

// Handle changing availability for a time slot
const handleChangeAvailability = (timeSlot) => {
  timeSlotForm.setFieldsValue({
    availability: timeSlot.availability,
  });
  Modal.confirm({
    title: `Change Availability for ${timeSlot.startTime} - ${timeSlot.endTime} on ${timeSlot.date}`,
    content: (
      <Form form={timeSlotForm} layout="vertical">
        <Form.Item
          name="availability"
          label="New Availability"
          rules={[{ required: true, message: 'Please enter the new availability!' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    ),
    onOk: () => {
      timeSlotForm
        .validateFields()
        .then(async (values) => {
          try {
            await updateTimeSlot(selectedService.id, timeSlot.id, { availability: values.availability });
            message.success('Availability updated successfully!');
            // Refresh time slots
            const timeSlotsData = await fetchTimeSlotsWithBookings(selectedService.id);
            setTimeSlots(timeSlotsData);
          } catch (error) {
            message.error('Failed to update availability.');
            // console.error('Error:', error);
          }
        })
        .catch((info) => {
          // console.log('Validate Failed:', info);
        });
    },
    onCancel() {},
    okText: 'Update',
    cancelText: 'Cancel',
  });
};


const handleDateSelect = (date) => {
  const selectedDate = date.format('YYYY-MM-DD');
  const slotsForDate = timeSlots.filter((ts) => ts.date === selectedDate);
  // Implement logic to display these slots, e.g., open a modal or populate a drawer
};


// Handle date selection on the calendarconst 
const onSelectDate = async (date) => {
  const selectedDateStr = date.format('YYYY-MM-DD');
  setSelectedDate(selectedDateStr);
  try {
    const timeSlotsData = await fetchTimeSlotsWithBookings(selectedService.id, selectedDateStr);
    setTimeSlots(timeSlotsData);
  } catch (error) {
    message.error('Failed to load time slots for the selected date.');
    // console.error('Error:', error);
  }
};




// Customize date cell rendering to show badges if time slots exist
const dateCellRender = (value) => {
  const dateStr = value.format('YYYY-MM-DD');
  const slotsForDate = timeSlots.filter((ts) => ts.date === dateStr);
  return slotsForDate.length > 0 ? (
    <Badge count={slotsForDate.length} style={{ backgroundColor: '#52c41a' }} />
  ) : null;
};






const renderBookingActions = (booking) => {
  const actions = [];

  // Only show "Mark as Completed" if booking is not already completed
  if (!booking.bookingStatus || booking.bookingStatus === 'Confirmed') {
    actions.push(
      <Button
        type="primary"
        icon={<CheckOutlined />}
        onClick={() => handleMarkBookingAsCompleted(booking)}
        size="small"
      >
        Mark as Completed
      </Button>
    );
  }

  actions.push(
    <Button
      icon={<MessageOutlined />}
      onClick={() => handleMessageUser(booking)}
      type="default"
      size="small"
    >
      Message
    </Button>
  );

  return actions;
};


const handleMarkBookingAsCompleted = async (booking) => {
  try {
    await updateBookingStatus(
      selectedService.id,
      booking.timeSlotId,
      booking.id,  // Ensure this is the correct booking ID from your data structure
      'Completed'
    );
    message.success('Booking marked as completed!');
    // Refresh time slots
    const updatedTimeSlots = await fetchTimeSlotsWithBookings(
      selectedService.id,
      selectedDate
    );
    setTimeSlots(updatedTimeSlots);
  } catch (error) {
    message.error('Failed to update booking status.');
    // console.error('Error:', error);
  }
};

async function updateBookingStatusForAll(serviceId, timeSlotId, status) {
  try {
    const bookingsRef = db.collection('services').doc(serviceId)
      .collection('timeSlots').doc(timeSlotId)
      .collection('bookings');

    const bookingsSnapshot = await bookingsRef.get();

    const batch = db.batch();

    bookingsSnapshot.forEach((doc) => {
      const bookingRef = bookingsRef.doc(doc.id);
      batch.update(bookingRef, { bookingStatus: status });
    });

    await batch.commit();
  } catch (error) {
    // console.error('Error updating booking statuses:', error);
    throw error;
  }
}

async function resetTimeSlotAvailability(serviceId, timeSlotId) {
  try {
    const timeSlotRef = db.collection('services').doc(serviceId).collection('timeSlots').doc(timeSlotId);

    // Reset availability to initial value (you might store the initial availability elsewhere)
    const timeSlotDoc = await timeSlotRef.get();
    if (timeSlotDoc.exists) {
      const timeSlotData = timeSlotDoc.data();

      await timeSlotRef.update({ purchase: 0 });
    }
  } catch (error) {
    // console.error('Error resetting time slot availability:', error);
    throw error;
  }
}
const handleCompleteAllBookings = async (timeSlotId) => {
  try {
    await updateBookingStatusForAll(selectedService.id, timeSlotId, 'Completed');
    message.success('All bookings marked as completed!');
    // Refresh time slots
    const updatedTimeSlots = await fetchTimeSlotsWithBookings(selectedService.id, selectedDate);
    setTimeSlots(updatedTimeSlots);
  } catch (error) {
    message.error('Failed to update bookings.');
    // console.error('Error:', error);
  }
};


const handleResetAvailability = async (timeSlotId) => {
  try {
    await resetTimeSlotAvailability(selectedService.id, timeSlotId);
    message.success('Availability reset successfully!');
    // Refresh time slots
    const updatedTimeSlots = await fetchTimeSlotsWithBookings(selectedService.id, selectedDate);
    setTimeSlots(updatedTimeSlots);
  } catch (error) {
    message.error('Failed to reset availability.');
    // console.error('Error:', error);
  }
};


const renderBookingsForTimeSlot = (timeSlot) => {
  // Get the current filter for this time slot, default to 'All'
  const currentFilter = bookingFiltersPerTimeSlot[timeSlot.id] || 'All';

  // Filter bookings based on currentFilter
  const filteredBookings = (timeSlot.bookings || []).filter((booking) => {
    if (currentFilter === 'All') return true;
    if (currentFilter === 'Confirmed') return !booking.bookingStatus || booking.bookingStatus === 'Confirmed';
    if (currentFilter === 'Completed') return booking.bookingStatus === 'Completed';
    return true;
  });

  return (
    <div>
      {/* Booking Filter Select for this Time Slot */}
      <Space style={{ marginBottom: 16 }}>
        <Select
          value={currentFilter}
          onChange={(value) => setBookingFiltersPerTimeSlot(prev => ({ ...prev, [timeSlot.id]: value }))}
          style={{ width: 200 }}
        >
          <Option value="All">All Bookings</Option>
          <Option value="Confirmed">Confirmed</Option>
          <Option value="Completed">Completed</Option>
        </Select>
      </Space>
      {/* Filtered Bookings List */}
      <List
        dataSource={filteredBookings}
        renderItem={(booking) => (
          <List.Item key={booking.id} actions={renderBookingActions(booking)}>
            <List.Item.Meta
              title={`${booking.customerName} `}
              description={`Email: ${booking.email}`}
            />
          </List.Item>
        )}
      />
    </div>
  );
};

// Combined bookings for all time slots on the selected date
const allBookings = timeSlots.flatMap((timeSlot) => 
  (timeSlot.bookings || []).map(booking => ({
    ...booking,
    timeSlotStart: timeSlot.startTime,
    timeSlotEnd: timeSlot.endTime
  }))
);


  const renderAllBookingsForDate = () => {
    // Filter bookings based on bookingFilterAllBookings
    const filteredBookings = allBookings.filter((booking) => {
      if (bookingFilterAllBookings === 'All') return true;
      if (bookingFilterAllBookings === 'Confirmed') return !booking.bookingStatus || booking.bookingStatus === 'Confirmed';
      if (bookingFilterAllBookings === 'Completed') return booking.bookingStatus === 'Completed';
      return true;
    });
  
    return (
      <div>
        {/* Booking Filter Select for All Bookings */}
        <Space style={{ marginBottom: 16 }}>
          <Select
            value={bookingFilterAllBookings}
            onChange={(value) => setBookingFilterAllBookings(value)}
            style={{ width: 200 }}
          >
            <Option value="All">All Bookings</Option>
            <Option value="Confirmed">Confirmed</Option>
            <Option value="Completed">Completed</Option>
          </Select>
        </Space>
        {/* Filtered Bookings List */}
        <List
          dataSource={filteredBookings}
          renderItem={(booking) => (
            <List.Item key={booking.id} actions={renderBookingActions(booking)}>
              <List.Item.Meta
                title={`${booking.customerName} `}
                description={`Time Slot: ${booking.timeSlotStart} - ${booking.timeSlotEnd}`}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };
  

   // Messaging State Variables
   const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
   const [messageContent, setMessageContent] = useState('');
   const [recipient, setRecipient] = useState(null); // null indicates individual user, can adjust based on requirement
 
  
   const handleMessageUser = async (booking) => {
    const user =  await getUserInfo(booking.userId);
    setRecipient(user);  // Set recipient as the booking user
    setIsMessageModalVisible(true); // Open the message modal
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      message.error('Message cannot be empty.');
      return;
    }
  
    setIsLoadingAction(true); // Start loading
  
    try {
      if (recipient) {
        // Sending message to a specific user
        await sendMessage(currentUser.uid, recipient.name, recipient.id, messageContent);
        message.success(`Message sent to ${recipient.name}`);
        
      } else {
        // Implement logic if you want to support messaging all users
        // For example, send individual messages to each user or use a broadcast mechanism
        message.warn('Messaging all users is not implemented.');
      }
      
      // Reset message content and close modal
      setMessageContent('');
      setIsMessageModalVisible(false);
    } catch (error) {
      message.error('Failed to send message.');
      // console.error('Error sending message:', error);
    } finally {
      setIsLoadingAction(false); // Stop loading
    }
  };

  
  
  if (loadingServices) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Spin tip="Loading services..." size="large" />
      </div>
    );
  }

  return (
    
    <Layout style={{ minHeight: '100vh' }}>


<Modal
  title={recipient ? `Send Message to ${recipient.customerName}` : 'Send Message'}
  visible={isMessageModalVisible}
  onOk={handleSendMessage}
  onCancel={() => setIsMessageModalVisible(false)}
  okText="Send"
  cancelText="Cancel"
  confirmLoading={isLoadingAction} // Show loading indicator on send button
>
  <Form layout="vertical">
    <Form.Item
      label="Message"
      rules={[{ required: true, message: 'Please enter your message!' }]}
    >
      <Input.TextArea
        rows={4}
        value={messageContent}
        onChange={(e) => setMessageContent(e.target.value)}
        placeholder="Type your message here..."
      />
    </Form.Item>
  </Form>
</Modal>


      {/* Main Content */}
      <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
        <Card
          title="Service Management"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateService}>
              Add Service
            </Button>
          }
        >
          {/* Services Table */}
          <Table
            columns={serviceColumns}
            dataSource={services}
            pagination={{ pageSize: 5 }}
            rowKey="id"
            bordered
          />
        </Card>

        {/* Service Modal for Add/Edit */}
        <Modal
          title={editingService ? 'Edit Service' : 'Add New Service'}
          visible={isServiceModalVisible}
          onCancel={() => setIsServiceModalVisible(false)}
          footer={null}
          destroyOnClose
          maskClosable={false} // Prevent closing when clicking outside
          bodyStyle={{ overflowY: 'auto' }} // Enable scrollbar
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleServiceFormSubmit}
            initialValues={editingService || {}}
          >
            <Form.Item
              name="name"
              label="Service Name"
              rules={[{ required: true, message: 'Please enter the service name!' }]}
            >
              <Input placeholder="Enter service name" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter the service description!' }]}
            >
              <Input.TextArea rows={4} placeholder="Enter service description" />
            </Form.Item>
            <Form.Item
              name="category"
              label="Categories"
              rules={[{ required: true, message: 'Please select categories!' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select categories"
                style={{ width: '100%' }}
              >
                <Option value="Arts and crafts">Arts and crafts</Option>
                <Option value="Holiday activities and clubs">Holiday activities and clubs</Option>
                <Option value="Libraries">Libraries</Option>
                <Option value="Museums and galleries">Museums and galleries</Option>
                <Option value="Music, dance, drama">Music, dance, drama</Option>
                <Option value="Parks and playgrounds">Parks and playgrounds</Option>
                <Option value="Respite services">Respite services</Option>
                <Option value="Overnight respite">Overnight respite</Option>
                <Option value="Sports and exercise">Sports and exercise</Option>
                <Option value="Youth Clubs">Youth Clubs</Option>
                <Option value="Online activities">Online activities</Option>
                <Option value="Parents and carers">Parents and carers</Option>
                <Option value="Young carers">Young carers</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="email"
              label="Contact Email"
              rules={[
                { required: true, message: 'Please enter the contact email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input placeholder="Enter contact email" />
            </Form.Item>
            <Form.Item
              name="address"
              label="Service Address"
              rules={[{ required: true, message: 'Please enter the service address!' }]}
            >
              <Input placeholder="Enter service address" />
            </Form.Item>
            <Form.Item
              name="phone_number"
              label="Phone Number"
              rules={[{ required: true, message: 'Please enter the phone number!' }]}

            >
              <Input placeholder="Enter phone number" />
            </Form.Item>
            <Form.Item
              name="website"
              label="Website"
              rules={[{ required: true, message: 'Please enter the website!' }]}

            >
              <Input placeholder="Enter website URL" />
            </Form.Item>
            <Form.Item
              name="activities"
              label="Activities"
              rules={[{ required: true, message: 'Please select activities!' }]}
            >
              <Select
                mode="tags"
                placeholder="Select or type to add new activities"
                style={{ width: '100%' }}
              >
                <Option value="Adaptive Sports">Adaptive Sports</Option>
                <Option value="Art Therapy">Art Therapy</Option>
                <Option value="Assistive Technology Training">Assistive Technology Training</Option>
                <Option value="Campaigning">Campaigning</Option>
                <Option value="Communication Skills">Communication Skills</Option>
                <Option value="Daily Living Skills">Daily Living Skills</Option>
                <Option value="Educational Support">Educational Support</Option>
                <Option value="Getting people into work">Getting people into work</Option>
                <Option value="Group Therapy">Group Therapy</Option>
                <Option value="Life Skills Training">Life Skills Training</Option>
                <Option value="Music Therapy">Music Therapy</Option>
                <Option value="Physical Therapy">Physical Therapy</Option>
                <Option value="Recreational Activities">Recreational Activities</Option>
                <Option value="Self-advocacy Training">Self-advocacy Training</Option>
                <Option value="Self-help groups">Self-help groups</Option>
                <Option value="Sensory Activities">Sensory Activities</Option>
                <Option value="Social Skills Development">Social Skills Development</Option>
                <Option value="Speech and Language Therapy">Speech and Language Therapy</Option>
                <Option value="Vocational Training">Vocational Training</Option>
                <Option value="Wellness and Exercise">Wellness and Exercise</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="beneficiaries"
              label="Beneficiaries"
              rules={[{ required: true, message: 'Please select beneficiaries!' }]}
            >
              <Select
                mode="tags"
                placeholder="Select or type to add new beneficiary groups"
                style={{ width: '100%' }}
              >
                <Option value="Adults with Learning Disabilities">Adults with Learning Disabilities</Option>
                <Option value="Adults with Physical Disabilities">Adults with Physical Disabilities</Option>
                <Option value="Adults with Mental Health Conditions">Adults with Mental Health Conditions</Option>
                <Option value="Autism and Neurodiversity">Autism and Neurodiversity</Option>
                <Option value="Blind or Visually Impaired">Blind or Visually Impaired</Option>
                <Option value="Carers">Carers</Option>
                <Option value="Children with Special Educational Needs">Children with Special Educational Needs</Option>
                <Option value="Deaf or Hard of Hearing">Deaf or Hard of Hearing</Option>
                <Option value="Elderly with Disabilities">Elderly with Disabilities</Option>
                <Option value="Family Members and Support Networks">Family Members and Support Networks</Option>
                <Option value="General Public">General Public</Option>
                <Option value="Long-term Health Conditions">Long-term Health Conditions</Option>
                <Option value="Multiple Disabilities">Multiple Disabilities</Option>
                <Option value="Parents of Disabled Children">Parents of Disabled Children</Option>
                <Option value="People with Communication Difficulties">People with Communication Difficulties</Option>
                <Option value="Sensory Processing Needs">Sensory Processing Needs</Option>
                <Option value="Teenagers with Disabilities">Teenagers with Disabilities</Option>
                <Option value="Veterans with Disabilities">Veterans with Disabilities</Option>
                <Option value="Women with Disabilities">Women with Disabilities</Option>
                <Option value="Young Adults with Disabilities">Young Adults with Disabilities</Option>
              </Select>
            </Form.Item>

            <Form.Item
  name="price"
  label="Price"
  rules={[{ required: true, message: 'Please enter the price!' }]}
>
  <InputNumber
    placeholder="Enter price"
    style={{ width: '100%' }}
    min={1}
    step={0.01}
    formatter={(value) => `$ ${value}`}
    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
  />
</Form.Item>


           {/* Service Image Upload */}
           <Form.Item
              name="image"
              label="Service Image"
              valuePropName="fileList"
              getValueFromEvent={(e) => Array.isArray(e) ? e : e && e.fileList}
              rules={[{ required: true, message: 'Please upload a service image!' }]}
            >
              <Upload
                name="image"
                listType="picture-card"
                beforeUpload={() => false} // Prevent automatic upload
                accept="image/*"

                onChange={(info) => {
                  const fileList = info.fileList.slice(-1); // Keep only the latest file
                  form.setFieldsValue({ image: fileList });
                  if (info.file.status === 'removed') {
                    setImagePreview(null);
                  } else {
                    const file = info.file.originFileObj;
                    if (file) {
                      setImagePreview(URL.createObjectURL(file));
                    } else if (info.file.url) {
                      setImagePreview(info.file.url);
                    }
                  }
                }}
              >
                {!imagePreview && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>

            {/* Marker Image Upload */}
            <Form.Item
              name="marker_image"
              label="Marker Image"
              valuePropName="fileList"
              getValueFromEvent={(e) => Array.isArray(e) ? e : e && e.fileList}
              rules={[{ required: true, message: 'Please upload a marker image!' }]}
            >
              <Upload
                name="markerImage"
                listType="picture-card"
                beforeUpload={() => false} // Prevent automatic upload
                accept="image/*"

                onChange={(info) => {
                  const fileList = info.fileList.slice(-1); // Keep only the latest file
                  form.setFieldsValue({ marker_image: fileList });
                  if (info.file.status === 'removed') {
                    setMarkerImagePreview(null);
                  } else {
                    const file = info.file.originFileObj;
                    if (file) {
                      setMarkerImagePreview(URL.createObjectURL(file));
                    } else if (info.file.url) {
                      setMarkerImagePreview(info.file.url);
                    }
                  }
                }}
              >
                {!markerImagePreview && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>


            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isLoadingAction}
                >
                  {editingService ? 'Update Service' : 'Create Service'}
                </Button>
                <Button onClick={() => setIsServiceModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>



        {/* drawer for booking */}
        
        {/* Manage Time Slots Drawer */}
        <Drawer
  title={`Manage Time Slots for ${selectedService?.name}`}
  placement="right"
  width={1000}
  onClose={() => setIsTimeSlotDrawerVisible(false)}
  visible={isTimeSlotDrawerVisible}
>
  <Space direction="vertical" style={{ width: '100%' }}>
    {/* Add Time Slot Button */}
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={handleAddTimeSlot}
      style={{ marginBottom: '16px' }}
    >
      Add Time Slot
    </Button>
    {/* Calendar Component */}
    <Calendar
      fullscreen={false}
      onSelect={onSelectDate}
      dateCellRender={dateCellRender}
    />
    {/* Selected Date Time Slots */}
    {selectedDate && (
      <Card title={`Time Slots for ${selectedDate}`}>
        <Tabs defaultActiveKey="all">
          <TabPane tab="All Bookings" key="all">
            {renderAllBookingsForDate()}
          </TabPane>
          {timeSlots.map((timeSlot) => (
            <TabPane
              tab={`${timeSlot.startTime} - ${timeSlot.endTime}`}
              key={timeSlot.id}
            >
              <Space style={{ marginBottom: '16px' }}>
        
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleCompleteAllBookings(timeSlot.id)}
                >
                  Complete All
                </Button>
                <Button
                  type="default"
                  icon={<SyncOutlined />}
                  onClick={() => handleResetAvailability(timeSlot.id)}
                >
                  Reset Availability
                </Button>
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => handleEditTimeSlot(timeSlot)}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Are you sure to delete this time slot? All associated bookings will also be deleted."
                  onConfirm={() => handleDeleteTimeSlot(timeSlot.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} type="danger">
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
              {/* Render Bookings for this Time Slot */}
              {renderBookingsForTimeSlot(timeSlot)}
            </TabPane>
          ))}
        </Tabs>
      </Card>
    )}
  </Space>
</Drawer>







      </Content>
    </Layout>
  );
};

export default BusinessServicePage;
